/**
 * Transcoders that turn a bwip-js SVG barcode into vector EPS (PostScript) and
 * vector PDF.
 *
 * bwip-js only produces SVG, PNG (canvas) and its own drawing primitives out of
 * the box. The SVG it emits is fully vector — the bars are stroked `<path>`
 * elements and the human-readable digits are filled glyph `<path>` elements — so
 * it is a convenient single geometry source. These functions parse that SVG and
 * re-emit the identical geometry as EPS and PDF, keeping both output formats true
 * vector (no rasterisation, no font embedding — glyphs are already outlined).
 *
 * The parser is intentionally minimal: it only understands the exact subset of
 * SVG that bwip-js generates (a `viewBox`, an optional background `<rect>`, and
 * `<path>` elements whose `d` uses absolute `M`, `L`, `Q`, `C` and `Z` commands).
 */
/** Parses the `d` attribute of an SVG path into its segments. */
var parsePathData = function (d) {
    var tokens = d.match(/[MLQCZ]|-?\d*\.?\d+(?:e-?\d+)?/gi) || [];
    var segs = [];
    var i = 0;
    var num = function () { return parseFloat(tokens[i++]); };
    while (i < tokens.length) {
        var cmd = tokens[i++];
        if (cmd === "M")
            segs.push({ t: "M", x: num(), y: num() });
        else if (cmd === "L")
            segs.push({ t: "L", x: num(), y: num() });
        else if (cmd === "Q")
            segs.push({ t: "Q", cx: num(), cy: num(), x: num(), y: num() });
        else if (cmd === "C")
            segs.push({
                t: "C",
                cx1: num(),
                cy1: num(),
                cx2: num(),
                cy2: num(),
                x: num(),
                y: num(),
            });
        else if (cmd === "Z")
            segs.push({ t: "Z" });
    }
    return segs;
};
/** Parses a bwip-js SVG string into its viewBox, background and path list. */
export var parseBarcodeSvg = function (svg) {
    var vb = svg.match(/viewBox="0 0 ([\d.]+) ([\d.]+)"/);
    if (!vb) {
        throw new Error("Unexpected barcode SVG: missing viewBox");
    }
    var width = parseFloat(vb[1]);
    var height = parseFloat(vb[2]);
    var rect = svg.match(/<rect[^>]*fill="#([0-9A-Fa-f]{6})"/);
    var bg = rect ? rect[1] : null;
    var paths = [];
    var re = /<path\b([^>]*)\/>/g;
    var m = re.exec(svg);
    while (m !== null) {
        var attrs = m[1];
        var dMatch = attrs.match(/\bd="([^"]*)"/);
        if (dMatch) {
            var stroke = attrs.match(/stroke="#([0-9A-Fa-f]{6})"/);
            var sw = attrs.match(/stroke-width="([\d.]+)"/);
            var fill = attrs.match(/\bfill="#([0-9A-Fa-f]{6})"/);
            paths.push({
                d: dMatch[1],
                stroke: stroke ? stroke[1] : null,
                strokeWidth: sw ? parseFloat(sw[1]) : 0,
                fill: fill ? fill[1] : null,
                evenodd: /fill-rule="evenodd"/.test(attrs),
            });
        }
        m = re.exec(svg);
    }
    return { width: width, height: height, bg: bg, paths: paths };
};
/** Converts a 6-digit hex colour into normalised (0..1) r/g/b components. */
var hexToRgb = function (hex) { return [
    parseInt(hex.slice(0, 2), 16) / 255,
    parseInt(hex.slice(2, 4), 16) / 255,
    parseInt(hex.slice(4, 6), 16) / 255,
]; };
/** Formats a number with at most three decimals and no trailing zeros. */
var f = function (n) { return (Math.round(n * 1000) / 1000).toString(); };
/**
 * Converts a quadratic Bézier (start, control, end) into the cubic control
 * points that EPS/PDF require (neither format has a native quadratic operator).
 */
var quadToCubic = function (x0, y0, cx, cy, x, y) { return [
    x0 + (2 / 3) * (cx - x0),
    y0 + (2 / 3) * (cy - y0),
    x + (2 / 3) * (cx - x),
    y + (2 / 3) * (cy - y),
    x,
    y,
]; };
/**
 * Transcodes a bwip-js SVG barcode into an Encapsulated PostScript (EPS) string.
 *
 * The output is a valid EPSF-3.0 document with a correct BoundingBox that opens
 * in any PostScript/PDF viewer. The SVG y-down coordinate space is flipped to the
 * PostScript y-up space via a translate + scale.
 *
 * @param svg the SVG produced by {@link generateBarcodeSvg}
 * @returns the EPS document as a string
 */
export var svgToEps = function (svg) {
    var _a = parseBarcodeSvg(svg), width = _a.width, height = _a.height, bg = _a.bg, paths = _a.paths;
    var out = "%!PS-Adobe-3.0 EPSF-3.0\n";
    out += "%%Creator: ui-br-agency (bwip-js)\n";
    out += "%%BoundingBox: 0 0 ".concat(Math.ceil(width), " ").concat(Math.ceil(height), "\n");
    out += "%%EndComments\n";
    out += "gsave\n0 ".concat(f(height), " translate\n1 -1 scale\n0 setlinecap\n");
    if (bg) {
        var _b = hexToRgb(bg), r = _b[0], g = _b[1], b = _b[2];
        out += "".concat(r, " ").concat(g, " ").concat(b, " setrgbcolor\n0 0 ").concat(f(width), " ").concat(f(height), " rectfill\n");
    }
    for (var _i = 0, paths_1 = paths; _i < paths_1.length; _i++) {
        var p = paths_1[_i];
        var px = 0;
        var py = 0;
        var ops = [];
        for (var _c = 0, _d = parsePathData(p.d); _c < _d.length; _c++) {
            var s = _d[_c];
            if (s.t === "M") {
                ops.push("".concat(f(s.x), " ").concat(f(s.y), " moveto"));
                px = s.x;
                py = s.y;
            }
            else if (s.t === "L") {
                ops.push("".concat(f(s.x), " ").concat(f(s.y), " lineto"));
                px = s.x;
                py = s.y;
            }
            else if (s.t === "Q") {
                var c = quadToCubic(px, py, s.cx, s.cy, s.x, s.y);
                ops.push("".concat(f(c[0]), " ").concat(f(c[1]), " ").concat(f(c[2]), " ").concat(f(c[3]), " ").concat(f(c[4]), " ").concat(f(c[5]), " curveto"));
                px = s.x;
                py = s.y;
            }
            else if (s.t === "C") {
                ops.push("".concat(f(s.cx1), " ").concat(f(s.cy1), " ").concat(f(s.cx2), " ").concat(f(s.cy2), " ").concat(f(s.x), " ").concat(f(s.y), " curveto"));
                px = s.x;
                py = s.y;
            }
            else {
                ops.push("closepath");
            }
        }
        if (ops.length && p.stroke) {
            var _e = hexToRgb(p.stroke), r = _e[0], g = _e[1], b = _e[2];
            out += "newpath\n".concat(ops.join("\n"), "\n").concat(r, " ").concat(g, " ").concat(b, " setrgbcolor\n").concat(f(p.strokeWidth), " setlinewidth\nstroke\n");
        }
        else if (ops.length) {
            var _f = hexToRgb(p.fill || "000000"), r = _f[0], g = _f[1], b = _f[2];
            out += "newpath\n".concat(ops.join("\n"), "\n").concat(r, " ").concat(g, " ").concat(b, " setrgbcolor\n").concat(p.evenodd ? "eofill" : "fill", "\n");
        }
    }
    out += "grestore\n%%EOF\n";
    return out;
};
/**
 * Transcodes a bwip-js SVG barcode into a single-page vector PDF string.
 *
 * The output is a minimal but valid PDF-1.4 document (catalog, pages, one page,
 * one content stream, cross-reference table) with a MediaBox matching the barcode
 * size. It is intentionally pure ASCII so it can be wrapped in a `Blob` without
 * byte-encoding concerns. The content stream flips the y axis (`1 0 0 -1 0 H cm`)
 * so the SVG coordinates can be used directly.
 *
 * @param svg the SVG produced by {@link generateBarcodeSvg}
 * @returns the PDF document as a string
 */
export var svgToPdf = function (svg) {
    var _a = parseBarcodeSvg(svg), width = _a.width, height = _a.height, bg = _a.bg, paths = _a.paths;
    var W = Math.ceil(width);
    var H = Math.ceil(height);
    var cs = "1 0 0 -1 0 ".concat(H, " cm\n0 J\n");
    if (bg) {
        var _b = hexToRgb(bg), r = _b[0], g = _b[1], b = _b[2];
        cs += "".concat(r, " ").concat(g, " ").concat(b, " rg\n0 0 ").concat(f(width), " ").concat(f(height), " re\nf\n");
    }
    for (var _i = 0, paths_2 = paths; _i < paths_2.length; _i++) {
        var p = paths_2[_i];
        var px = 0;
        var py = 0;
        var ops = [];
        for (var _c = 0, _d = parsePathData(p.d); _c < _d.length; _c++) {
            var s = _d[_c];
            if (s.t === "M") {
                ops.push("".concat(f(s.x), " ").concat(f(s.y), " m"));
                px = s.x;
                py = s.y;
            }
            else if (s.t === "L") {
                ops.push("".concat(f(s.x), " ").concat(f(s.y), " l"));
                px = s.x;
                py = s.y;
            }
            else if (s.t === "Q") {
                var c = quadToCubic(px, py, s.cx, s.cy, s.x, s.y);
                ops.push("".concat(f(c[0]), " ").concat(f(c[1]), " ").concat(f(c[2]), " ").concat(f(c[3]), " ").concat(f(c[4]), " ").concat(f(c[5]), " c"));
                px = s.x;
                py = s.y;
            }
            else if (s.t === "C") {
                ops.push("".concat(f(s.cx1), " ").concat(f(s.cy1), " ").concat(f(s.cx2), " ").concat(f(s.cy2), " ").concat(f(s.x), " ").concat(f(s.y), " c"));
                px = s.x;
                py = s.y;
            }
            else {
                ops.push("h");
            }
        }
        if (ops.length && p.stroke) {
            var _e = hexToRgb(p.stroke), r = _e[0], g = _e[1], b = _e[2];
            cs += "".concat(r, " ").concat(g, " ").concat(b, " RG\n").concat(f(p.strokeWidth), " w\n").concat(ops.join("\n"), "\nS\n");
        }
        else if (ops.length) {
            var _f = hexToRgb(p.fill || "000000"), r = _f[0], g = _f[1], b = _f[2];
            cs += "".concat(r, " ").concat(g, " ").concat(b, " rg\n").concat(ops.join("\n"), "\n").concat(p.evenodd ? "f*" : "f", "\n");
        }
    }
    var objs = [
        "<< /Type /Catalog /Pages 2 0 R >>",
        "<< /Type /Pages /Kids [3 0 R] /Count 1 >>",
        "<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ".concat(W, " ").concat(H, "] /Contents 4 0 R /Resources << >> >>"),
        "<< /Length ".concat(cs.length, " >>\nstream\n").concat(cs, "\nendstream"),
    ];
    var pdf = "%PDF-1.4\n";
    var offsets = [];
    for (var i = 0; i < objs.length; i++) {
        offsets.push(pdf.length);
        pdf += "".concat(i + 1, " 0 obj\n").concat(objs[i], "\nendobj\n");
    }
    var xref = pdf.length;
    pdf += "xref\n0 ".concat(objs.length + 1, "\n0000000000 65535 f \n");
    for (var _g = 0, offsets_1 = offsets; _g < offsets_1.length; _g++) {
        var off = offsets_1[_g];
        pdf += "".concat(String(off).padStart(10, "0"), " 00000 n \n");
    }
    pdf += "trailer\n<< /Size ".concat(objs.length + 1, " /Root 1 0 R >>\nstartxref\n").concat(xref, "\n%%EOF\n");
    return pdf;
};
